import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { ActionStatus as Action_Status_Enum } from '@risksmart-app/domain/src/types/consts/action-status';
import type { ApprovalGatedResult } from '@risksmart-app/trpc/src/helpers/with-approval-workflow';
import type { UpdateActionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateActionDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { unwrapApprovalGated } from '@/hooks/mutations/unwrapApprovalGated';
import { createMutationHook } from '@/utils/hook-factories';

export interface UpdateActionInput {
  Id: string;
  Title: string;
  DateDue: string;
  DateRaised: string;
  Status: Action_Status_Enum;
  Priority?: number | null;
  Description?: string | null;
  ClosedDate?: string | null;
  OriginalTimestamp: string;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds: string[];
  OwnerGroupIds: string[];
  ContributorUserIds: string[];
  ContributorGroupIds: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds: string[];
  DepartmentTypeIds: string[];
}

interface UpdateActionOptions {
  confirmChangeRequest?: boolean;
  hasFileChanges?: boolean;
}

/**
 * Maps the tRPC mutation response to the GraphQL mutation structure. The applied
 * entity is discarded — the legacy shape reports a fixed affected-row count.
 */
const mapTrpcResponseToGraphQL = (_applied: {
  Id: string;
}): UpdateActionMutation => ({
  updateChildAction: {
    __typename: 'UpdateChildActionOutput',
    affected_rows: 1,
    change_request_id: null,
  },
});

export const useUpdateAction = createMutationHook({
  name: 'updateAction',
  isChangeRequestGated: true,
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.action.update.mutationOptions({ onSuccess }),
  // The public input already matches the tRPC procedure input — passthrough.
  mapInput: (input: UpdateActionInput) => input,
  // RSP-5789 — when the mutation was gated into a change request (created or
  // amended), surface it on the CR read feeds immediately.
  refreshChangeRequestFeedsWhen: (
    result: ApprovalGatedResult<{ Id: string }>
  ) => result.status !== 'applied',
  invalidate: (trpc) => [
    trpc.frontend.action.register.queryKey(),
    trpc.frontend.action.actionById.queryKey(),
    // COI-889: COI-471 parity — the Apollo twin evicts `control`, whose
    // registers carry open/closed-action count columns.
    trpc.frontend.control.pathKey(),
    // COI-889: linked-item reads embed the linked entity's title
    // (`linked-item.query.ts`), which this hook rewrites.
    trpc.frontend.linkedItem.linkedItems.queryKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
    // COI-889: this hook rewrites the owner/contributor sets, whose triggers
    // maintain the grant cache `perm_v2_entity_permissions` reads (60-min
    // window, `refetchOnMount: false`, so it never self-heals).
    trpc.frontend.permission.getEntityPermissions.queryKey(),
  ],
  // A confirmed update resolves as `applied` OR raises a CR — both are success.
  // Echo the input Id on the CR branch (the entity is unchanged).
  mapResponse: (
    result: ApprovalGatedResult<{ Id: string }>,
    input: UpdateActionInput
  ) => mapTrpcResponseToGraphQL(unwrapApprovalGated(result, { Id: input.Id })),
  graphqlDocument: UpdateActionDocument,
  graphqlVariables: (input: UpdateActionInput) => ({ ...input }),
  evictFields: ['action', 'action_aggregate', 'control'],
  refetchQueries: [namedOperations.Query.getActionById],
  apolloContext: (options: UpdateActionOptions | undefined) => ({
    headers: {
      'x-confirm-change-request': options?.confirmChangeRequest
        ? 'true'
        : 'false',
      'x-has-file-changes': options?.hasFileChanges ? 'true' : 'false',
    },
  }),
});
